import {
  ChevronsUpDown, CircleUser, LogOut, Moon, Monitor, PanelLeftClose, PanelLeftOpen, PhoneCall, Sun,
} from "lucide-react";
import * as React from "react";
import {
  Link, NavLink, Outlet, useLocation, useMatch, useResolvedPath,
} from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, Separator, Tooltip, TooltipProvider } from "@/components/ui/misc";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/feedback";
import { PageBackContext } from "@/components/layout/page";
import { useTheme } from "@/components/layout/theme";
import { useSoftphone } from "@/softphone/softphone-context";
import { SoftphoneDock } from "@/softphone/softphone-dock";
import { SignedInAsBar } from "@/features/signed-in-as-bar";
import { useAuth } from "@/lib/auth";
import { LiveHints } from "@/lib/live-hints";
import {
  ACCOUNT_ITEM, SETTINGS_ITEM, hasSettings, isSettingsPath, settingsBackLink, visibleSections,
  type NavItem,
} from "@/lib/nav";
import { useStages } from "@/lib/queries";
import { setStageCatalog } from "@/lib/stages";
import { ROLE_LABELS } from "@/lib/types";
import { cn } from "@/lib/utils";

/** Whether the sidebar is folded to its icons. This browser only, like the phone's keypad
 *  preference: how much room a screen can spare for the nav is a fact about the screen. */
const NAV_COLLAPSED_KEY = "acl.nav.collapsed";

function readNavCollapsed(): boolean {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

function writeNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(NAV_COLLAPSED_KEY, String(collapsed));
  } catch {
    /* non-fatal — the sidebar simply opens expanded after a reload */
  }
}

export function AppShell() {
  const { user } = useAuth();
  const phone = useSoftphone();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(readNavCollapsed);
  // The tenant's stages (ADR-077 in `platform`). Every stage label and picker reads them, as they
  // render, from `lib/stages.ts`, so the registry is set here, before the page below renders, and
  // the page waits for the first answer rather than drawing the built-in names and then the
  // tenant's. A failure does not hold it up: the built-in thirteen answer while it retries.
  const stages = useStages();
  setStageCatalog(stages.data);
  const stagesSettled = !stages.isPending || stages.failureCount > 0;

  const sections = visibleSections(user?.role);
  // The pages behind Settings have no link of their own here, so they get one back over their title.
  const back = React.useMemo(
    () => settingsBackLink(user?.role, location.pathname),
    [user?.role, location.pathname],
  );
  const toggleNav = () => {
    writeNavCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="app-frame flex h-dvh flex-col overflow-hidden bg-canvas">
        {/* Only while an admin is signed in as somebody else (ADR-040 in `platform`). */}
        <SignedInAsBar />
        <div className="app-frame flex min-h-0 flex-1 overflow-hidden">
          <aside
            className={cn(
              "hidden shrink-0 flex-col overflow-hidden border-r border-border bg-surface md:flex print:hidden",
              "transition-[width] duration-150",
              collapsed ? "w-14" : "w-56",
            )}
          >
            <div
              className={cn(
                "flex h-12 shrink-0 items-center gap-2 border-b border-border",
                collapsed ? "justify-center" : "pl-4 pr-2",
              )}
            >
              {!collapsed && (
                <>
                  <div className="flex size-6 shrink-0 items-center justify-center rounded bg-accent-9 text-white">
                    <PhoneCall className="size-3.5" aria-hidden />
                  </div>
                  <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm font-semibold tracking-tight">
                    AI Case Leads
                  </span>
                </>
              )}
              {/* One button, in one place in the tree for both widths, so keyboard focus stays on it
                  while the sidebar changes size around it. */}
              <Tooltip content={collapsed ? "Expand the sidebar" : "Collapse the sidebar"} side="right">
                <button
                  type="button"
                  onClick={toggleNav}
                  aria-expanded={!collapsed}
                  aria-label={collapsed ? "Expand the sidebar" : "Collapse the sidebar"}
                  className={cn(
                    "group flex shrink-0 items-center justify-center rounded-md text-text-muted transition-colors",
                    "hover:bg-surface-hover hover:text-text focus-visible:bg-surface-hover",
                    collapsed ? "size-8" : "size-7",
                  )}
                >
                  {collapsed ? (
                    <>
                      {/* Folded, the button is the logo until a pointer or the keyboard reaches it,
                          and then shows what clicking it does. */}
                      <span className="flex size-6 items-center justify-center rounded bg-accent-9 text-white group-hover:hidden group-focus-visible:hidden">
                        <PhoneCall className="size-3.5" aria-hidden />
                      </span>
                      <PanelLeftOpen
                        className="hidden size-4 group-hover:block group-focus-visible:block"
                        aria-hidden
                      />
                    </>
                  ) : (
                    <PanelLeftClose className="size-4" aria-hidden />
                  )}
                </button>
              </Tooltip>
            </div>

            <nav
              className={cn(
                "relative flex-1 overflow-y-auto overflow-x-hidden py-4 scrollbar-subtle",
                collapsed ? "space-y-3 px-2" : "space-y-5 px-3",
              )}
            >
              {sections.map((section, index) => (
                <div key={section.heading}>
                  {collapsed ? (
                    // The headings need the width the sidebar gave up; a rule keeps the groups apart.
                    index > 0 && <div className="mx-2 mb-3 border-t border-border" aria-hidden />
                  ) : (
                    <p className="whitespace-nowrap px-2 pb-1.5 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                      {section.heading}
                    </p>
                  )}
                  <ul className="space-y-0.5">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <SidebarLink item={item} collapsed={collapsed} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>

            <div className="shrink-0 space-y-1 border-t border-border p-2">
              {/* Everything set up once and changed now and then, one click down (`lib/nav.ts`). At
                  the foot rather than in the list, so it is in the same place however long the
                  list above it grows. Indented to line its icon up with the links above. */}
              {hasSettings(user?.role) && (
                <div className={cn(!collapsed && "px-1")}>
                  <SidebarLink
                    item={SETTINGS_ITEM}
                    collapsed={collapsed}
                    active={isSettingsPath(location.pathname)}
                  />
                </div>
              )}
              <UserMenu compact={collapsed} side={collapsed ? "right" : "top"} />
            </div>
          </aside>

          <div className="flex min-w-0 flex-1 flex-col">
            {/* The mobile header carries the identity and the menu the sidebar holds on desktop. */}
            <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-border bg-surface px-4 md:hidden print:hidden">
              <span className="flex items-center gap-2 text-sm font-semibold">
                <span className="flex size-6 items-center justify-center rounded bg-accent-9 text-white">
                  <PhoneCall className="size-3.5" aria-hidden />
                </span>
                AI Case Leads
              </span>
              <UserMenu compact />
            </header>

            <main
              // Reset the scroll position on navigation by keying on the path; otherwise moving
              // from a scrolled list to a short page lands you halfway down an empty screen.
              key={location.pathname}
              // `relative` makes this the containing block of anything absolutely positioned in a
              // screen — every `sr-only` label is. Without it those are placed against the page,
              // at their spot in a long table, and push the document past the frame: a second
              // scrollbar and white space under the sidebar (Endpoints, 2026-09-28).
              className="app-frame relative min-h-0 flex-1 overflow-y-auto scrollbar-subtle"
            >
              {stagesSettled ? (
                <PageBackContext.Provider value={back}>
                  <Outlet />
                </PageBackContext.Provider>
              ) : (
                <div className="flex h-32 items-center justify-center">
                  <Spinner />
                </div>
              )}
              {/* Below `md` the phone floats over the bottom-right corner, so every page gets room
                  to scroll its last row out from under it. Wider, the phone has a column of its
                  own and covers nothing. */}
              {phone.available && <div aria-hidden className="h-16 md:hidden" />}
            </main>
          </div>

          {/* Last in the row: from `md` up, the phone is the layout's right-hand column. */}
          <div className="contents print:hidden">
            <SoftphoneDock />
          </div>
          {/* Renders nothing: the one subscription to the refetch hints, while somebody is in. */}
          <LiveHints />
        </div>
      </div>
    </TooltipProvider>
  );
}

/**
 * One link in the sidebar, at either width.
 *
 * The class name is worked out here and handed over as a STRING. Folded, the link sits inside a
 * tooltip, whose trigger is a Radix Slot, and a Slot joins its own class name with the child's as
 * text: `NavLink`'s function form (`className={({ isActive }) => …}`) came out the other side as
 * the function's source code. The folded rail then had no classes at all — icons pushed to the
 * left edge, rows taller than the rail was drawn for, and no mark on the page you were on.
 */
function SidebarLink({
  item, collapsed, active,
}: {
  item: NavItem;
  collapsed: boolean;
  /** Whether this is the page you are on, when that is more than its own path: Settings is marked
   *  on every page behind it. */
  active?: boolean;
}) {
  const path = useResolvedPath(item.to);
  // What `NavLink` matches on without `end`: this page, or anything under it.
  const matched = useMatch({ path: path.pathname, end: false }) !== null;
  const isActive = active ?? matched;

  return (
    <Tooltip
      content={collapsed ? (item.badge ? `${item.label} · ${item.badge}` : item.label) : null}
      side="right"
    >
      <Link
        to={item.to}
        aria-current={isActive ? "page" : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={cn(
          "flex items-center rounded-md text-sm transition-colors",
          collapsed ? "h-9 justify-center" : "gap-2.5 whitespace-nowrap px-2 py-1.5",
          isActive
            ? "bg-accent-3 font-medium text-accent-11"
            : "text-text-muted hover:bg-surface-hover hover:text-text",
        )}
      >
        <item.icon className="size-4 shrink-0" aria-hidden />
        {!collapsed && item.label}
        {!collapsed && item.badge && (
          <Badge tone="outline" size="sm" className="ml-auto">
            {item.badge}
          </Badge>
        )}
      </Link>
    </Tooltip>
  );
}

function UserMenu({
  compact = false, side,
}: {
  compact?: boolean;
  /** Where the menu opens. Defaults to below the avatar in the mobile header and above it in the
   *  sidebar; the folded sidebar opens it to the right, beside a column too narrow to hold it. */
  side?: "top" | "right" | "bottom";
}) {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  if (!user) return null;

  const menuSide = side ?? (compact ? "bottom" : "top");
  const themes = [
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
    { value: "system", label: "System", icon: Monitor },
  ] as const;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          "flex w-full items-center gap-2 rounded-md p-1.5 text-left transition-colors",
          "hover:bg-surface-hover focus-visible:bg-surface-hover",
        )}
      >
        <Avatar name={user.display_name} email={user.email} />
        {!compact && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-medium text-text">
                {user.display_name || user.email}
              </span>
              <span className="block truncate text-2xs text-text-muted">
                {ROLE_LABELS[user.role]}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-text-subtle" aria-hidden />
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align={menuSide === "right" ? "end" : "start"}
        side={menuSide}
        className="w-56"
      >
        <DropdownMenuLabel>Signed in</DropdownMenuLabel>
        <div className="px-2 pb-2">
          <p className="truncate text-sm text-text">{user.display_name || user.email}</p>
          <p className="truncate text-xs text-text-muted">{user.email}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            <Badge tone="accent" size="sm">
              {ROLE_LABELS[user.role]}
            </Badge>
            {user.domain_name && (
              <Badge tone="outline" size="sm" className="font-mono">
                {user.domain_name}
              </Badge>
            )}
          </div>
        </div>
        <Separator />
        <DropdownMenuItem asChild>
          <NavLink to={ACCOUNT_ITEM.to}>
            <CircleUser aria-hidden />
            Account settings
          </NavLink>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        {themes.map((t) => (
          <DropdownMenuItem
            key={t.value}
            onSelect={(e) => {
              // Keep the menu open so the effect of each option can be compared without
              // reopening it three times.
              e.preventDefault();
              setTheme(t.value);
            }}
            className={cn(theme === t.value && "bg-surface-hover font-medium")}
          >
            <t.icon aria-hidden />
            {t.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void logout()}>
          <LogOut aria-hidden />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
